import { ApiError, API_URL, authToken, downloadFile, graphqlRequest } from "./client";
import { PLATFORM_FIELDS, type KycStatus, type Platform, type Track, type UserRole } from "./types";

export interface GigAdminRow {
  userId: string;
  firstName: string;
  lastName: string;
  participantId?: string | null;
  email: string;
  track?: Track | null;
  stepsDone: number[];
  bidsCounted: number;
  bidReadiness?: number | null;
  kycPlatformId?: string | null;
  kycPlatformName?: string | null;
  kycStatus?: KycStatus | null;
  hasKycScreenshot: boolean;
  hasCv: boolean;
  qualityPct?: number | null;
  verdict?: string | null;
  auditScore?: number | null;
  stalledDays?: number | null;
  lastLogin?: string | null;
}

export interface GigDashboard {
  participants: number;
  workEnabled: number;
  pathwayComplete: number;
  stalled: number;
  rosterSize: number;
  total: number;
  rows: GigAdminRow[];
}

export interface FinanceAdminRow {
  userId: string;
  firstName: string;
  lastName: string;
  participantId?: string | null;
  email: string;
  stepsDone: number[];
  services: string[];
  idDocsReady: number;
  moneyDocsReady: number;
  tier?: string | null;
  referredAt?: string | null;
}

export interface FinanceDashboard {
  participants: number;
  referred: number;
  total: number;
  rows: FinanceAdminRow[];
}

export interface AdminPlatform extends Platform {
  hosts: string[];
  active: boolean;
  sortOrder: number;
}

export type GigStage = "notStarted" | "inProgress" | "workEnabled" | "complete";
export type FinanceStage = "notStarted" | "inProgress" | "ready" | "referred";

export interface AdminFilter {
  search?: string;
  track?: Track | null;
  gigStage?: GigStage | null;
  financeStage?: FinanceStage | null;
  stalledOnly?: boolean;
  limit: number;
  pageNumber: number;
}

export const ADMIN_PAGE_SIZE = 25;

/* Drops empty values so the server's defaults apply. */
const filterInput = (filter: AdminFilter) =>
  Object.fromEntries(Object.entries(filter).filter(([, value]) => value !== "" && value != null));

const GIG_ROW_FIELDS = `userId firstName lastName participantId email track stepsDone bidsCounted bidReadiness kycPlatformId
  kycPlatformName kycStatus hasKycScreenshot hasCv qualityPct verdict auditScore stalledDays lastLogin`;
const FINANCE_ROW_FIELDS =
  "userId firstName lastName participantId email stepsDone services idDocsReady moneyDocsReady tier referredAt";

/** Gig readiness dashboard. */
export async function fetchGigDashboard(filter: AdminFilter): Promise<GigDashboard> {
  return (
    await graphqlRequest<{ getGigDashboard: GigDashboard }>(
      `query($filter: AdminFilterInput!) { getGigDashboard(filter: $filter) {
        participants workEnabled pathwayComplete stalled rosterSize total rows { ${GIG_ROW_FIELDS} } } }`,
      { variables: { filter: filterInput(filter) } },
    )
  ).getGigDashboard;
}

/** Access to Finance dashboard. */
export async function fetchFinanceDashboard(filter: AdminFilter): Promise<FinanceDashboard> {
  return (
    await graphqlRequest<{ getFinanceDashboard: FinanceDashboard }>(
      `query($filter: AdminFilterInput!) { getFinanceDashboard(filter: $filter) {
        participants referred total rows { ${FINANCE_ROW_FIELDS} } } }`,
      { variables: { filter: filterInput(filter) } },
    )
  ).getFinanceDashboard;
}

export interface ParticipantDetail {
  gig: GigAdminRow;
  finance: FinanceAdminRow;
  niche?: string | null;
  portfolioUrl?: string | null;
  cvName?: string | null;
  platforms: {
    platformId: string;
    name: string;
    profileUrl?: string | null;
    verified: boolean;
    kycStatus?: KycStatus | null;
    hasKycScreenshot: boolean;
  }[];
  gigTimeline: { step: number; name: string; completedAt?: string | null }[];
  financeTimeline: { step: number; name: string; completedAt?: string | null }[];
  joinedAt: string;
}

/** One participant, for the side panel. */
export async function fetchParticipantDetail(userId: string): Promise<ParticipantDetail> {
  return (
    await graphqlRequest<{ getParticipantDetail: ParticipantDetail }>(
      `query($userId: String!) { getParticipantDetail(userId: $userId) {
        gig { ${GIG_ROW_FIELDS} } finance { ${FINANCE_ROW_FIELDS} } niche portfolioUrl cvName joinedAt
        platforms { platformId name profileUrl verified kycStatus hasKycScreenshot }
        gigTimeline { step name completedAt } financeTimeline { step name completedAt } } }`,
      { variables: { userId } },
    )
  ).getParticipantDetail;
}

/* ---------- view-only files ---------- */

export type ViewedFile =
  | { kind: "pdf"; data: ArrayBuffer }
  | { kind: "image"; url: string }
  | { kind: "html"; html: string }
  | { kind: "unsupported" };

/**
 * Fetches a participant file for display only. The API streams it with the admin's own session (no shareable link);
 * the bytes stay in memory and are drawn by the viewer, never offered to the browser as a download.
 */
async function viewFile(path: string): Promise<ViewedFile> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      headers: { Authorization: `Bearer ${authToken.get() ?? ""}` },
    });
  } catch {
    throw new ApiError(
      0,
      "UNREACHABLE",
      "We couldn't load that file. Check your connection and try again.",
    );
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? "UNKNOWN",
      body?.error?.message ?? "We couldn't load that file.",
    );
  }
  const type = response.headers.get("content-type") ?? "";
  if (type.startsWith("application/pdf"))
    return { kind: "pdf", data: await response.arrayBuffer() };
  if (type.startsWith("image/"))
    return { kind: "image", url: URL.createObjectURL(await response.blob()) };
  const json = (await response.json()) as { kind: "html" | "unsupported"; html?: string };
  return json.kind === "html" ? { kind: "html", html: json.html ?? "" } : { kind: "unsupported" };
}

export function viewParticipantCv(userId: string): Promise<ViewedFile> {
  return viewFile(`/api/admin/view/cv/${encodeURIComponent(userId)}`);
}

export function viewKycScreenshot(userId: string, platformId: string): Promise<ViewedFile> {
  return viewFile(
    `/api/admin/view/kyc/${encodeURIComponent(userId)}/${encodeURIComponent(platformId)}`,
  );
}

/** Delete a participant and all their data (SuperAdmin). */
export async function deleteParticipant(userId: string): Promise<void> {
  await graphqlRequest(`mutation($userId: String!) { deleteParticipant(userId: $userId) }`, {
    variables: { userId },
  });
}

/** All platforms, including inactive ones. */
export async function fetchAdminPlatforms(): Promise<AdminPlatform[]> {
  return (
    await graphqlRequest<{ getPlatforms: AdminPlatform[] }>(
      `{ getPlatforms(includeInactive: true) { ${PLATFORM_FIELDS} hosts active sortOrder } }`,
    )
  ).getPlatforms;
}

export interface PlatformEdit {
  id: string;
  name?: string;
  description?: string;
  referralUrl?: string;
  hosts?: string[];
  active?: boolean;
}

/** Edit a platform, e.g. its referral link. */
export async function updatePlatform(input: PlatformEdit): Promise<AdminPlatform> {
  return (
    await graphqlRequest<{ updatePlatform: AdminPlatform }>(
      `mutation($input: UpdatePlatformInput!) { updatePlatform(input: $input) { ${PLATFORM_FIELDS} hosts active sortOrder } }`,
      { variables: { input } },
    )
  ).updatePlatform;
}

export interface NewPlatform {
  key: string;
  name: string;
  description: string;
  referralUrl: string;
  hosts: string[];
}

/** Add a partner platform. */
export async function createPlatform(input: NewPlatform): Promise<AdminPlatform> {
  return (
    await graphqlRequest<{ createPlatform: AdminPlatform }>(
      `mutation($input: PlatformInput!) { createPlatform(input: $input) { ${PLATFORM_FIELDS} hosts active sortOrder } }`,
      { variables: { input } },
    )
  ).createPlatform;
}

/** Number of roster entries (0 means the roster is off). */
export async function fetchRosterSize(): Promise<number> {
  return (await graphqlRequest<{ rosterSize: number }>(`{ rosterSize }`)).rosterSize;
}

/** Load roster entries. Returns the new roster size. */
export async function importRoster(
  entries: { email: string; participantId?: string | undefined }[],
  replace: boolean,
): Promise<number> {
  return (
    await graphqlRequest<{ importRoster: number }>(
      `mutation($input: ImportRosterInput!) { importRoster(input: $input) }`,
      { variables: { input: { entries, replace } } },
    )
  ).importRoster;
}

export type ExportKind = "gig" | "gig-gate-log" | "finance" | "finance-gate-log";

const EXPORT_FILES: Record<ExportKind, string> = {
  gig: "gateway-gig-readiness.csv",
  "gig-gate-log": "gateway-gig-gate-log.csv",
  finance: "gateway-access-to-finance.csv",
  "finance-gate-log": "gateway-finance-gate-log.csv",
};

/** Download a CSV export for weekly LMS reconciliation. */
export function downloadExport(kind: ExportKind): Promise<void> {
  return downloadFile(`/api/admin/export/${kind}`, EXPORT_FILES[kind]);
}

/* ---------- admin accounts ---------- */

export interface AdminAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  lastLogin?: string | null;
  mustChangePassword: boolean;
  createdAt: string;
}

const ADMIN_ACCOUNT_FIELDS =
  "id email firstName lastName role lastLogin mustChangePassword createdAt";

export async function fetchAdmins(): Promise<AdminAccount[]> {
  return (
    await graphqlRequest<{ getAdmins: AdminAccount[] }>(`{ getAdmins { ${ADMIN_ACCOUNT_FIELDS} } }`)
  ).getAdmins;
}

export interface TemporaryPassword {
  user: AdminAccount;
  temporaryPassword: string;
}

export async function inviteAdmin(input: {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}): Promise<TemporaryPassword> {
  return (
    await graphqlRequest<{ inviteAdmin: TemporaryPassword }>(
      `mutation($input: InviteAdminInput!) { inviteAdmin(input: $input) { temporaryPassword user { ${ADMIN_ACCOUNT_FIELDS} } } }`,
      { variables: { input } },
    )
  ).inviteAdmin;
}

export async function resetAdminPassword(userId: string): Promise<TemporaryPassword> {
  return (
    await graphqlRequest<{ resetAdminPassword: TemporaryPassword }>(
      `mutation($userId: String!) { resetAdminPassword(userId: $userId) { temporaryPassword user { ${ADMIN_ACCOUNT_FIELDS} } } }`,
      { variables: { userId } },
    )
  ).resetAdminPassword;
}

/* ---------- activity ---------- */

export type AdminAction =
  | "signIn"
  | "signInFailed"
  | "passwordChanged"
  | "passwordResetRequested"
  | "passwordReset"
  | "passwordResetByAdmin"
  | "adminInvited"
  | "dataExported"
  | "rosterImported"
  | "documentViewed"
  | "participantDeleted";

export interface AdminActivityEntry {
  id: string;
  action: AdminAction;
  actorName: string;
  actorEmail: string;
  summary: string;
  targetLabel?: string | null;
  ip?: string | null;
  createdAt: string;
}

export const ACTIVITY_PAGE_SIZE = 50;

export async function fetchAdminActivity(filter: {
  action?: AdminAction | null;
  search?: string;
  pageNumber: number;
}): Promise<{ total: number; rows: AdminActivityEntry[] }> {
  return (
    await graphqlRequest<{ getAdminActivity: { total: number; rows: AdminActivityEntry[] } }>(
      `query($filter: ActivityFilterInput!) { getAdminActivity(filter: $filter) { total rows { id action actorName actorEmail summary targetLabel ip createdAt } } }`,
      { variables: { filter: { ...filter, limit: ACTIVITY_PAGE_SIZE } } },
    )
  ).getAdminActivity;
}
